import { FeatureSwitchSettings } from '../../_models/admin/feature-switch.models';
import { BrandInfo } from '../../_models/reports/brand-info.model';

/** "1 Nov" in the viewer's own time zone. */
export function shortDate(iso: string | null): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function brandLabel(id: number, brands: BrandInfo[]): string {
  const brand = (brands || []).find(b => b.companyId === id);
  return (brand && (brand.brandShortCode || brand.name)) || ('brand ' + id);
}

function users(n: number): string {
  return n + (n === 1 ? ' user' : ' users');
}

/**
 * One line describing who a switch is on for, e.g. "On · 30% · roles TCMGR · FES · until 1 Nov".
 * Only settings that narrow the audience appear; a plain "On" means everyone.
 */
export function describeSwitch(s: FeatureSwitchSettings | null, brands: BrandInfo[] = []): string {
  if (!s) {
    return '';
  }

  return [s.isEnabled ? 'On' : 'Off', ...targetingParts(s, brands)].join(' · ');
}

/**
 * Who the switch is aimed at, without its on/off state (the row's toggle shows that), e.g.
 * "30% · roles TCMGR · FES · until 1 Nov". "Everyone" when nothing narrows it.
 */
export function describeTargeting(s: FeatureSwitchSettings | null, brands: BrandInfo[] = []): string {
  if (!s) {
    return '';
  }

  const parts = targetingParts(s, brands);
  return parts.length ? parts.join(' · ') : 'Everyone';
}

function targetingParts(s: FeatureSwitchSettings, brands: BrandInfo[]): string[] {
  const parts: string[] = [];

  if (s.rolloutPercent != null && s.rolloutPercent < 100) {
    parts.push(s.rolloutPercent + '%');
  }
  if (s.targetRoles && s.targetRoles.length) {
    parts.push('roles ' + s.targetRoles.join(', '));
  }
  if (s.targetBrandIds && s.targetBrandIds.length) {
    parts.push(s.targetBrandIds.map(id => brandLabel(id, brands)).join(', '));
  }
  if (s.includeUserIds && s.includeUserIds.length) {
    parts.push('+' + users(s.includeUserIds.length));
  }
  if (s.excludeUserIds && s.excludeUserIds.length) {
    parts.push('−' + users(s.excludeUserIds.length));
  }
  if (s.startUtc) {
    parts.push('from ' + shortDate(s.startUtc));
  }
  if (s.endUtc) {
    parts.push('until ' + shortDate(s.endUtc));
  }

  return parts;
}
